import React, { useRef, useState, useEffect } from "react";
import { array, string, bool } from "prop-types";
import { ResponsiveImage } from "@quintype/components";
import SmallArrowLeft from "../../../../assets/icons/small-arrow-left";
import SmallArrowRight from "../../../../assets/icons/small-arrow-right";
import Chevron from "../../../../assets/icons/chevron";
import "./event-gallery.m.css";

// Event galleries are full-bleed and often viewed on high-DPR displays.
// Request larger variants at higher quality to avoid visible softness.
const GALLERY_WIDTHS = [480, 768, 1024, 1280, 1440];
const GALLERY_IMG_PARAMS = { auto: ["format"], q: 100 };

export const EventGallery = ({ galleryImages = [], morePhotosUrl = "#", morePhotosText = "More Photos", openInNewTab = false, useBlackBackground = true }) => {
    const sliderRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const totalImages = galleryImages.length;
    const intervalRef = useRef(null);

    // Auto-slide functionality
    const startAutoSlide = () => {
        if (totalImages > 1) {
            intervalRef.current = setInterval(() => {
                setCurrentIndex((prev) => (prev < totalImages - 1 ? prev + 1 : 0));
            }, 4000);
        }
    };

    const resetAutoSlide = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        startAutoSlide();
    };

    useEffect(() => {
        startAutoSlide();
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [totalImages]);

    // Simplified state management for fade transition
    const handlePrev = () => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : totalImages - 1));
        resetAutoSlide();
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev < totalImages - 1 ? prev + 1 : 0));
        resetAutoSlide();
    };

    const renderGalleryImage = (image, index) => {
        const imageS3Key = image?.["image-s3-key"] || image?.["s3-key"];
        const imageMetadata = image?.metadata || image?.["image-metadata"] || {};
        const imageUrl = image?.url;

        return (
            <div key={index} styleName={index === currentIndex ? "slider-slide active-slide" : "slider-slide"}>
                {imageS3Key ? (
                    <ResponsiveImage
                        slug={imageS3Key}
                        metadata={imageMetadata}
                        defaultWidth={1024}
                        widths={GALLERY_WIDTHS}
                        imgParams={GALLERY_IMG_PARAMS}
                        alt={image?.title || "Gallery image"}
                    />
                ) : imageUrl ? (
                    <img src={imageUrl} alt={image?.title || "Gallery image"} style={{ width: '100%', height: 'auto' }} />
                ) : (
                    <div style={{ color: '#ccc' }}>Image not found</div>
                )}
            </div>
        );
    };

    return (
        <div styleName="event-gallery" data-section="gallery">
            <div
                styleName={useBlackBackground ? "gallery-container black-gallery-container target-gallery-container" : "gallery-container"}
                style={useBlackBackground ? { backgroundColor: "#000000" } : undefined}
            >
                <div styleName={useBlackBackground ? "slider-wrapper target-slider-wrapper" : "slider-wrapper"}>
                    <div styleName="slider-content" ref={sliderRef}>
                        {galleryImages.map((image, index) => renderGalleryImage(image, index))}
                    </div>

                    {totalImages > 1 && (
                        <>
                            <button
                                styleName="nav-arrow prev"
                                onClick={handlePrev}
                                aria-label="Previous slide"
                            >
                                <div styleName="arrow-icon prev">
                                    <SmallArrowLeft />
                                </div>
                            </button>
                            <button
                                styleName="nav-arrow next"
                                onClick={handleNext}
                                aria-label="Next slide"
                            >
                                <div styleName="arrow-icon next">
                                    <SmallArrowRight />
                                </div>
                            </button>
                        </>
                    )}

                    {totalImages > 1 && (
                        <div styleName="slider-meta" aria-live="polite">
                            <div styleName="slide-counter">{currentIndex + 1}/{totalImages}</div>
                            <div styleName="dot-track" role="tablist" aria-label="Gallery slide indicators">
                                {galleryImages.map((_, index) => (
                                    <button
                                        key={`dot-${index}`}
                                        type="button"
                                        styleName={index === currentIndex ? "dot active-dot" : "dot"}
                                        onClick={() => {
                                            setCurrentIndex(index);
                                            resetAutoSlide();
                                        }}
                                        aria-label={`Go to slide ${index + 1}`}
                                        aria-selected={index === currentIndex}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {morePhotosUrl && morePhotosUrl !== "#" && (
                    <div styleName={useBlackBackground ? "more-photos-wrapper target-more-photos-wrapper" : "more-photos-wrapper"}>
                        <div styleName="more-photos-container">
                            <a
                                href={morePhotosUrl.startsWith("www") ? `https://${morePhotosUrl}` : morePhotosUrl}
                                styleName="more-photos-link"
                                {...(openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                            >
                                <div styleName="more-photos-text">{morePhotosText}</div>
                                <div styleName="more-photos-icon">
                                    <Chevron color="#FF2626" />
                                </div>
                            </a>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

EventGallery.propTypes = {
    galleryImages: array,
    morePhotosUrl: string,
    morePhotosText: string,
    openInNewTab: bool,
    useBlackBackground: bool,
};
